import { useState, useEffect } from 'react';
import { Loader2, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { accessControlAction } from '@/lib/api';

interface StripePaymentProps {
  deviceId: string;
  amount: number; // öre
  onPaid: (paymentId: string) => void;
  onCancel: () => void;
}

type Status = 'idle' | 'creating' | 'ready' | 'processing' | 'succeeded' | 'failed';

export function StripePayment({ deviceId, amount, onPaid, onCancel }: StripePaymentProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const createPayment = async () => {
    setStatus('creating');
    setError(null);

    const result = await accessControlAction<{
      payment_id: string;
      client_secret: string;
      status: string;
      using_connect: boolean;
    }>('create-access-payment', {
      device_id: deviceId,
      payment_method: 'stripe',
    });

    if (result.error || !result.data) {
      setError(result.error || 'Kunde inte skapa betalning');
      setStatus('failed');
      return;
    }

    setPaymentId(result.data.payment_id);
    setClientSecret(result.data.client_secret);
    setStatus('ready');
  };

  const confirmPayment = async () => {
    if (!clientSecret) return;
    setStatus('processing');

    try {
      const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
      if (!stripeKey) {
        setError('Stripe är inte konfigurerat');
        setStatus('failed');
        return;
      }

      // Dynamic import to avoid bundling Stripe if not used
      const { loadStripe } = await import('@stripe/stripe-js');
      const stripe = await loadStripe(stripeKey);
      if (!stripe) {
        setError('Kunde inte ladda Stripe');
        setStatus('failed');
        return;
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: { token: '' } as any, // Stripe Elements would provide the card element
        },
      });

      if (stripeError) {
        setError(stripeError.message || 'Betalning misslyckades');
        setStatus('failed');
        return;
      }

      if (paymentIntent?.status === 'succeeded' && paymentId) {
        setStatus('succeeded');
        onPaid(paymentId);
      }
    } catch (e: any) {
      setError(e.message || 'Betalning misslyckades');
      setStatus('failed');
    }
  };

  const amountKr = (amount / 100).toFixed(2);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Kortbetalning</h3>
      <p className="text-sm text-muted-foreground">
        Belopp: <strong>{amountKr} kr</strong>
      </p>

      {status === 'idle' && (
        <Button onClick={createPayment} className="w-full">
          <CreditCard className="h-4 w-4 mr-2" />
          Betala {amountKr} kr med kort
        </Button>
      )}

      {status === 'creating' && (
        <div className="flex items-center justify-center gap-2 py-4">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Förbereder betalning...</span>
        </div>
      )}

      {status === 'ready' && (
        <div className="space-y-3">
          <p className="text-sm">Klicka för att genomföra betalningen:</p>
          <Button onClick={confirmPayment} className="w-full">
            <CreditCard className="h-4 w-4 mr-2" />
            Bekräfta betalning
          </Button>
        </div>
      )}

      {status === 'processing' && (
        <div className="flex items-center justify-center gap-2 py-4">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Bearbetar betalning...</span>
        </div>
      )}

      {status === 'failed' && (
        <div className="space-y-3">
          <p className="text-sm text-destructive">{error}</p>
          <Button onClick={createPayment} variant="outline" className="w-full">
            Försök igen
          </Button>
        </div>
      )}

      {status !== 'succeeded' && status !== 'processing' && (
        <Button variant="ghost" onClick={onCancel} className="w-full">
          Avbryt
        </Button>
      )}
    </div>
  );
}
